/**
 * MONSTER TOWN — PICKING. ARCHITECT-OWNED, FROZEN. Additive only.
 *
 * Turns a canvas tap into (a) a world point on the ground plane of the active
 * scene, or (b) the placed instance a kid actually meant to touch.
 *
 * TOLERANT PICKING is a design requirement, not a nicety. A 3-year-old aims at
 * the middle of a chair and hits the floor. `pickInstance` therefore falls back
 * to "nearest placed thing within `slopPx` of the tap" before giving up.
 */

import * as THREE from 'three'
import type { Cell } from '../core/grid'
import type { SceneNode } from '../core/scene'
import type { InstanceId, Vec3 } from '../core/types'
import type { CanvasPoint } from './touch'

const raycaster = new THREE.Raycaster()
const ndc = new THREE.Vector2()
const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
const hitPoint = new THREE.Vector3()
const projected = new THREE.Vector3()

/** World point where the tap ray crosses the horizontal plane at height `y`. */
export function pickGround(
  camera: THREE.Camera,
  p: CanvasPoint,
  y = 0,
): Vec3 | null {
  ndc.set(p.ndcX, p.ndcY)
  raycaster.setFromCamera(ndc, camera)
  plane.set(new THREE.Vector3(0, 1, 0), -y)
  const hit = raycaster.ray.intersectPlane(plane, hitPoint)
  if (hit === null) return null
  return { x: hit.x, y: hit.y, z: hit.z }
}

/** Ground pick, converted straight to a grid cell in the active scene. */
export function pickCell(camera: THREE.Camera, p: CanvasPoint, node: SceneNode, gy = 0): Cell | null {
  const w = pickGround(camera, p, node.grid.levelY(gy))
  if (w === null) return null
  return node.grid.worldToCell(w.x, w.z, gy)
}

export interface InstancePick {
  instanceId: InstanceId
  /** World point on the object (or its centre, for a slop pick). */
  point: Vec3
  /** true when this came from the forgiving nearest-object fallback. */
  approximate: boolean
}

/**
 * Find the placed instance under the tap. Walks up from the hit mesh to the
 * per-instance group (whose `userData.instanceId` the build crew sets).
 *
 * `slopPx` — if the ray misses everything, take the nearest instance whose
 * screen-projected centre is within this many CSS pixels. Set to 0 to disable.
 */
export function pickInstance(
  camera: THREE.Camera,
  p: CanvasPoint,
  node: SceneNode,
  canvas: HTMLCanvasElement,
  slopPx = 44,
): InstancePick | null {
  ndc.set(p.ndcX, p.ndcY)
  raycaster.setFromCamera(ndc, camera)
  const hits = raycaster.intersectObject(node.placeables, true)
  for (const h of hits) {
    const id = instanceIdOf(h.object)
    if (id !== null) {
      return { instanceId: id, point: { x: h.point.x, y: h.point.y, z: h.point.z }, approximate: false }
    }
  }
  if (slopPx <= 0) return null

  const rect = canvas.getBoundingClientRect()
  const halfW = rect.width / 2
  const halfH = rect.height / 2
  let best: InstancePick | null = null
  let bestDist = slopPx
  for (const [id, art] of node.art) {
    art.getWorldPosition(projected)
    const world = { x: projected.x, y: projected.y, z: projected.z }
    projected.project(camera)
    const sx = (projected.x + 1) * halfW
    const sy = (-projected.y + 1) * halfH
    const d = Math.hypot(sx - p.x, sy - p.y)
    if (d < bestDist) {
      bestDist = d
      best = { instanceId: id, point: world, approximate: true }
    }
  }
  return best
}

/** Walk up the parent chain to the group tagged with an instance id. */
export function instanceIdOf(obj: THREE.Object3D): InstanceId | null {
  let cur: THREE.Object3D | null = obj
  let guard = 0
  while (cur !== null && guard++ < 32) {
    const id = cur.userData['instanceId']
    if (typeof id === 'string' && id.length > 0) return id as InstanceId
    cur = cur.parent
  }
  return null
}

/** Tag an instance art group so `pickInstance` can find it. Build crew calls this. */
export function tagInstance(obj: THREE.Object3D, id: InstanceId): void {
  obj.userData['instanceId'] = id
}

/** Screen position (canvas CSS px) of a world point — for floating coin/heart UI. */
export function worldToCanvasPx(
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
  world: Vec3,
): { x: number; y: number; behind: boolean } {
  projected.set(world.x, world.y, world.z).project(camera)
  const rect = canvas.getBoundingClientRect()
  return {
    x: (projected.x + 1) * (rect.width / 2),
    y: (-projected.y + 1) * (rect.height / 2),
    behind: projected.z > 1,
  }
}
